import React from "react";
import { ArrowRight, ChevronDown, MapPin } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { buildWhatsAppLink } from "@/lib/contact";
import { CONTACT } from "@/lib/contact";

const HERO_IMAGE = "/Highlight.png";

export const Hero = () => {
  const { t } = useLang();
  const waLink = buildWhatsAppLink(t.whatsappMessage);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end overflow-hidden bg-[var(--ms-night)]"
    >
      {/* Background image with slow zoom */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt={t.hero.imageAlt}
          fetchPriority="high"
          className="w-full h-full object-cover slow-zoom"
        />
      </div>

      {/* Gradient overlays */}
      <div className="absolute inset-0 hero-overlay z-10" />

      {/* Content */}
      <div className="relative z-20 w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pb-20 md:pb-28 pt-32">
        <div className="max-w-4xl fade-up">
          <span
            data-testid="hero-overline"
            className="ornament text-[#D4AF37] text-[10px] md:text-xs"
          >
            {t.hero.overline}
          </span>

          {/* In Vilamoura button (prominent) */}
          <div className="mt-6">
            <a
              href={CONTACT.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-in-vilamoura-btn"
              className="inline-flex items-center gap-2 bg-white/15 backdrop-blur border border-white/30 text-white hover:bg-white hover:text-[#003366] transition-colors duration-300 px-4 py-3 text-xs font-semibold tracking-[0.15em] uppercase"
            >
              <MapPin className="h-3.5 w-3.5 text-[#D4AF37]" />
              {t.inVilamoura}
            </a>
          </div>

          <h1
            data-testid="hero-headline"
            className="mt-4 font-serif text-white text-[clamp(2.75rem,7vw,6.5rem)] leading-[0.95] font-light tracking-tight"
          >
            <span className="block text-[#D4AF37] italic font-medium">
              {t.hero.headlineAccent}
            </span>
            <span className="block mt-2">{t.hero.headline}</span>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-8 text-white/80 text-base md:text-lg max-w-2xl leading-relaxed font-light"
          >
            {t.hero.subheadline}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <a
              href="#menu"
              data-testid="hero-view-menu-btn"
              className="inline-flex items-center justify-center gap-2 border border-white/40 text-white hover:bg-white hover:text-[#0047AB] transition-colors duration-300 px-8 py-4 uppercase tracking-[0.2em] text-[11px] font-semibold"
            >
              {t.hero.ctaMenu}
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-table-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#e5c454] transition-colors duration-300 px-8 py-4 uppercase tracking-[0.2em] text-[11px] font-semibold"
            >
              {t.hero.ctaBook}
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="hidden md:flex absolute bottom-8 right-12 items-center gap-3 text-white/60 text-[10px] tracking-[0.3em] uppercase">
          <span>{t.hero.scroll}</span>
          <ChevronDown className="h-4 w-4 animate-bounce" />
        </div>
      </div>
    </section>
  );
};
